import comp from '../components/barrel';
import svg from '../js/svg';

const EP_VERSIONS = 'wo/get-will-elements-version';
const EP_MAIN_ELEMENTS = 'wo/get_main_elements';
const EP_ELEMENT_BREAKDOWN = 'wo/get_element_breakdown';
const EP_UPDATE_BREAKDOWN = 'wo/upd_element_breakdown';
const EP_MIGRATE_VERSION = 'wo/migrate_version_number';
const EP_ADD_WILL_ELEMENT = 'wo/add-will-element';

export default {
  versions: [],
  mainElements: [],
  selectedVersion: null,

  async prepare() {
    this.recipe.methods.push(await O.mPOST(EP_VERSIONS, {}));
  },

  async render() {
    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },

  async loadPage(data) {
    const res = data[EP_VERSIONS];
    await this.renderVersionDropdown(res);
  },

  async renderVersionDropdown(res) {
    const panel = U.id('woElementsVersionContent');
    if (!panel) return;

    panel.innerHTML = '';

    if (!res || res.status !== 'OK') {
      panel.innerHTML = res?.message || res?.status || 'Unable to load will elements versions.';
      return;
    }

    this.versions = Array.isArray(res.data) ? res.data : [];

    if (!this.versions.length) {
      panel.innerHTML = 'No will element versions available.';
      return;
    }

    const toolbar = U.divAtt('div', 'class', 'flex flex-wrap items-end gap-4');

    const versionSelect = await comp.select(
      'woVersionSelect',
      this.versions,
      'version_number',
      'version_number',
      'Narrative version',
      0
    );
    toolbar.appendChild(versionSelect);

    const migrateInput = await comp.inputText('woMigrateVersion', '', 'Migrate to new version', 0);
    toolbar.appendChild(migrateInput);

    const migrateBtnWrap = U.divAtt('div', 'class', 'input-group');
    const migrateBtnLabel = U.divAtt('label');
    migrateBtnLabel.textContent = 'Migrate to new version';
    migrateBtnLabel.style.visibility = 'hidden';
    migrateBtnLabel.setAttribute('aria-hidden', 'true');
    const migrateBtn = U.divAtt('button', 'class', 'btn');
    migrateBtn.type = 'button';
    migrateBtn.textContent = 'Migrate';
    migrateBtn.style.marginTop = '0.25rem';
    migrateBtn.style.marginBottom = '1.25rem';
    migrateBtn.setAttribute('onclick', 'thsPage.migrateVersion()');
    migrateBtnWrap.appendChild(migrateBtnLabel);
    migrateBtnWrap.appendChild(migrateBtn);
    toolbar.appendChild(migrateBtnWrap);

    panel.appendChild(toolbar);

    const selectEl = U.id('woVersionSelect');
    const selectDiv = U.id('woVersionSelectDiv');
    const migrateInputEl = U.id('woMigrateVersion');
    const migrateInputDiv = U.id('woMigrateVersionDiv');
    if (selectEl) {
      if (selectEl.options.length) {
        selectEl.options[0].textContent = 'Choose version';
      }
      selectEl.style.width = '20ch';
      selectEl.style.maxWidth = '20ch';
      selectEl.addEventListener('change', () => this.onVersionChange());
    }
    if (selectDiv) {
      selectDiv.style.width = 'fit-content';
      selectDiv.style.maxWidth = 'fit-content';
    }
    if (migrateInputEl) {
      migrateInputEl.style.width = '20ch';
      migrateInputEl.style.maxWidth = '20ch';
    }
    if (migrateInputDiv) {
      migrateInputDiv.style.width = 'fit-content';
      migrateInputDiv.style.maxWidth = 'fit-content';
    }

    this.clearMainElements();
  },

  async migrateVersion() {
    const targetVersion = U.id('woMigrateVersion')?.value?.trim();
    const sourceVersion = U.id('woVersionSelect')?.value?.trim();

    if (!targetVersion) {
      comp.toast('Enter a value for Migrate to new version.');
      return;
    }

    if (!sourceVersion) {
      comp.toast('Choose a narrative version to migrate from.');
      return;
    }

    const vars = {
      source_version_number: sourceVersion,
      target_version_number: targetVersion,
    };

    try {
      const data = await O.PREP([await O.mPOST(EP_MIGRATE_VERSION, vars)]);
      const res = data[EP_MIGRATE_VERSION];
      comp.toast(res?.status || 'Migrated');

      if (res?.status === 'OK') {
        const refresh = await O.PREP([await O.mPOST(EP_VERSIONS, {})]);
        await this.renderVersionDropdown(refresh[EP_VERSIONS]);
        if (U.id('woVersionSelect')) {
          U.id('woVersionSelect').value = targetVersion;
          await this.onVersionChange();
        }
      }
    } catch {
      comp.toast('Unable to migrate version.');
    }
  },

  async onVersionChange() {
    const selectEl = U.id('woVersionSelect');
    const version = selectEl?.value?.trim();

    if (!version) {
      this.selectedVersion = null;
      this.clearMainElements();
      return;
    }

    await this.loadMainElements(version);
  },

  async loadMainElements(version) {
    const container = U.id('woElementsContent');
    if (!container) return;

    this.selectedVersion = version;
    container.innerHTML = '<p class="text-sm text-gray-600">Loading will elements…</p>';

    try {
      const data = await O.PREP([
        await O.mPOST(EP_MAIN_ELEMENTS, { version_number: version }),
      ]);
      const res = data[EP_MAIN_ELEMENTS];

      if (!res || res.status !== 'OK') {
        container.innerHTML = res?.message || res?.status || 'Unable to load will elements.';
        return;
      }

      this.mainElements = Array.isArray(res.data) ? res.data : [];
      await this.renderMainElementsList();
    } catch {
      container.innerHTML = 'Unable to load will elements.';
    }
  },

  clearMainElements() {
    this.mainElements = [];
    const container = U.id('woElementsContent');
    if (container) {
      container.innerHTML = '';
    }
  },

  async renderMainElementsList() {
    const container = U.id('woElementsContent');
    if (!container) return;

    container.innerHTML = '';

    const list = U.divAtt('div', 'class', 'flex flex-col gap-4');
    list.appendChild(await this.buildAddElementAccordion());

    if (!this.mainElements.length) {
      const empty = U.divAtt('p', 'class', 'text-sm text-gray-600');
      empty.textContent = 'No will elements found for this version.';
      list.appendChild(empty);
      container.appendChild(list);
      return;
    }

    const sorted = [...this.mainElements].sort(
      (a, b) => (Number(a.will_order) || 0) - (Number(b.will_order) || 0)
    );

    for (let i = 0; i < sorted.length; i += 1) {
      list.appendChild(await this.buildElementAccordion(sorted[i], i));
    }

    container.appendChild(list);
  },

  async buildAddElementAccordion() {
    const elID = 'woAddElement';
    const card = U.divAtt('div', 'class', 'card mb-0');
    card.id = elID;

    const header = U.divAtt('div', 'class', 'flex justify-between items-center gap-4 cursor-pointer');
    header.setAttribute('onclick', `thsPage.toggleWoElementAccordion('${elID}')`);

    const title = U.divAtt('h5', 'class', 'mb-0');
    title.textContent = 'Add will element';

    const accorArrow = await svg.chevronUp(`${elID}_arrow`);
    accorArrow.classList.add('arrowDown', 'shrink-0');

    header.appendChild(title);
    header.appendChild(accorArrow);
    card.appendChild(header);

    const contentDiv = U.divAtt('div', 'class', 'hide mt-4');
    contentDiv.id = `${elID}_content`;
    contentDiv.appendChild(await this.buildAddElementForm());
    card.appendChild(contentDiv);

    return card;
  },

  async buildAddElementForm() {
    const form = U.divAtt('div', 'class', 'border border-gray-200 rounded p-4');
    form.id = 'woAddElementForm';

    const versionNumber = this.selectedVersion || U.id('woVersionSelect')?.value || '';
    const versionField = comp.displayText('woAdd_version_number', versionNumber || '—', 'Version number');

    const name = await comp.inputText('woAdd_name', '', 'Name', 0);
    const willOrder = await comp.inputNumber('woAdd_will_order', '', 'Will order', 0);
    const willSubOrder = await comp.inputNumber('woAdd_will_sub_order', '', 'Will sub order', 0);
    const listOrderHeading = await comp.inputText('woAdd_list_order_heading', '', 'List order heading', 0);
    const body = await comp.textarea('woAdd_body', '', 'Body', 0);
    const subBody = await comp.textarea('woAdd_sub_body', '', 'Sub body', 0);

    const topRow = U.divAtt('div', 'class', 'flex flex-wrap items-end gap-4 mb-2');
    topRow.appendChild(name);
    topRow.appendChild(willOrder);
    topRow.appendChild(willSubOrder);
    topRow.appendChild(versionField);
    form.appendChild(topRow);

    const grid = U.divAtt('div', 'class', 'res-grid-cols-2');
    grid.appendChild(listOrderHeading);
    grid.appendChild(body);
    grid.appendChild(subBody);
    form.appendChild(grid);

    const actions = U.divAtt('div', 'class', 'flex justify-end mt-4');
    const addBtn = U.divAtt('button', 'class', 'btn');
    addBtn.type = 'button';
    addBtn.textContent = 'Add new element';
    addBtn.setAttribute('onclick', 'thsPage.addWillElement()');
    actions.appendChild(addBtn);
    form.appendChild(actions);

    return form;
  },

  async addWillElement() {
    const version_number = this.selectedVersion || U.id('woVersionSelect')?.value?.trim();
    if (!version_number) {
      comp.toast('Choose a narrative version first.');
      return;
    }

    const name = U.id('woAdd_name')?.value?.trim() ?? '';
    const willOrderRaw = U.id('woAdd_will_order')?.value;
    const willSubOrderRaw = U.id('woAdd_will_sub_order')?.value;
    const list_order_heading = U.id('woAdd_list_order_heading')?.value ?? '';
    const body = U.id('woAdd_body')?.value ?? '';
    const subBodyRaw = U.id('woAdd_sub_body')?.value;

    if (!name) {
      comp.toast('Enter a name.');
      return;
    }
    if (willOrderRaw === '' || willOrderRaw == null) {
      comp.toast('Enter a will order.');
      return;
    }
    if (willSubOrderRaw === '' || willSubOrderRaw == null) {
      comp.toast('Enter a will sub order.');
      return;
    }

    const vars = {
      name,
      will_order: Number(willOrderRaw),
      will_sub_order: Number(willSubOrderRaw),
      list_order_heading,
      body,
      sub_body: subBodyRaw === '' || subBodyRaw == null ? null : subBodyRaw,
      version_number,
    };

    try {
      const data = await O.PREP([await O.mPOST(EP_ADD_WILL_ELEMENT, vars)]);
      const res = data[EP_ADD_WILL_ELEMENT];
      comp.toast(res?.status || 'Added');

      if (res?.status === 'OK') {
        await this.loadMainElements(version_number);
      }
    } catch {
      comp.toast('Unable to add will element.');
    }
  },

  async buildElementAccordion(row, index) {
    const elID = `woElement_${index}`;
    const card = U.divAtt('div', 'class', 'card mb-0');
    card.id = elID;
    card.dataset.elementName = row.name || '';
    card.dataset.versionNumber = String(row.version_number ?? this.selectedVersion ?? '');

    const header = U.divAtt('div', 'class', 'flex justify-between items-start gap-4 cursor-pointer');
    header.setAttribute('onclick', `thsPage.toggleWoElementAccordion('${elID}')`);

    const heading = U.divAtt('div', 'class', 'flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4');
    this.appendAccordionHeadingField(heading, 'Name', row.name);
    this.appendAccordionHeadingField(heading, 'Description', row.description);

    const willOrder = U.divAtt('div', 'class', 'shrink-0 self-center text-right');
    const willOrderValue = `${row.will_order ?? '—'}/${row.will_sub_order ?? '—'}`;
    this.appendAccordionHeadingField(willOrder, 'Will order', willOrderValue);

    const activeIconWrap = U.divAtt('div', 'class', 'shrink-0 self-center');
    const isActive = !!row.active_yn;
    const activeIcon = isActive ? svg.tickCircle() : svg.crossCircle();
    activeIcon.classList.add('w-6', 'h-6');
    activeIcon.setAttribute('title', isActive ? 'Active' : 'Inactive');
    activeIcon.setAttribute('aria-label', isActive ? 'Active' : 'Inactive');
    activeIcon.style.color = isActive ? '#16a34a' : '#dc2626';
    activeIcon.setAttribute('stroke', isActive ? '#16a34a' : '#dc2626');
    activeIconWrap.appendChild(activeIcon);

    const accorArrow = await svg.chevronUp(`${elID}_arrow`);
    accorArrow.classList.add('arrowDown', 'shrink-0');

    header.appendChild(heading);
    header.appendChild(willOrder);
    header.appendChild(activeIconWrap);
    header.appendChild(accorArrow);
    card.appendChild(header);

    const contentDiv = U.divAtt('div', 'class', 'hide mt-4');
    contentDiv.id = `${elID}_content`;

    const breakdownDiv = U.divAtt('div');
    breakdownDiv.id = `${elID}_breakdown`;
    contentDiv.appendChild(breakdownDiv);

    card.appendChild(contentDiv);

    return card;
  },

  appendAccordionHeadingField(parent, label, value) {
    const row = U.divAtt('div');
    const lbl = U.divAtt('strong');
    lbl.textContent = `${label}: `;
    row.appendChild(lbl);
    row.appendChild(document.createTextNode(value != null && value !== '' ? String(value) : '—'));
    parent.appendChild(row);
  },

  async toggleWoElementAccordion(elID) {
    const contentDiv = U.id(`${elID}_content`);
    const arrow = U.id(`${elID}_arrow`);

    if (!contentDiv || !arrow) return;

    if (contentDiv.classList.contains('hide')) {
      U.unhide(`${elID}_content`);
      arrow.classList.remove('arrowDown');
      if (elID !== 'woAddElement') {
        await this.loadElementBreakdown(elID);
      }
    } else {
      U.hide(`${elID}_content`);
      arrow.classList.add('arrowDown');
    }
  },

  async loadElementBreakdown(elID) {
    const card = U.id(elID);
    const breakdownDiv = U.id(`${elID}_breakdown`);
    if (!card || !breakdownDiv) return;

    if (breakdownDiv.dataset.loaded === '1') return;

    const name = card.dataset.elementName;
    const version_number = card.dataset.versionNumber;

    if (!name || !version_number) {
      breakdownDiv.innerHTML = '<p class="text-sm text-gray-600">Missing element name or version.</p>';
      return;
    }

    breakdownDiv.innerHTML = '<p class="text-sm text-gray-600">Loading element breakdown…</p>';

    try {
      const data = await O.PREP([
        await O.mPOST(EP_ELEMENT_BREAKDOWN, { name, version_number }),
      ]);
      const res = data[EP_ELEMENT_BREAKDOWN];

      if (!res || res.status !== 'OK') {
        breakdownDiv.innerHTML = res?.message || res?.status || 'Unable to load element breakdown.';
        return;
      }

      breakdownDiv.dataset.loaded = '1';
      await this.renderElementBreakdown(breakdownDiv, res.data);
    } catch {
      breakdownDiv.innerHTML = 'Unable to load element breakdown.';
    }
  },

  async renderElementBreakdown(container, items) {
    container.innerHTML = '';

    const rows = Array.isArray(items) ? items : [];
    if (!rows.length) {
      container.innerHTML = '<p class="text-sm text-gray-600">No breakdown items found.</p>';
      return;
    }

    const sorted = [...rows].sort(
      (a, b) => (Number(a.will_sub_order) || 0) - (Number(b.will_sub_order) || 0)
    );

    const list = U.divAtt('div', 'class', 'flex flex-col gap-4');
    for (const item of sorted) {
      list.appendChild(await this.buildBreakdownForm(item));
    }
    container.appendChild(list);
  },

  async buildBreakdownForm(item) {
    const id = item.wo_will_elements_id;
    const form = U.divAtt('div', 'class', 'border border-gray-200 rounded p-4');
    form.id = `woBreakdownForm_${id}`;

    const grid = U.divAtt('div', 'class', 'res-grid-cols-2');

    const idField = comp.displayText(
      `woBreakdown_${id}_wo_will_elements_id`,
      item.wo_will_elements_id ?? '—',
      'WO will elements ID'
    );

    const willSubOrder = await comp.inputNumber(
      `woBreakdown_${id}_will_sub_order`,
      item.will_sub_order,
      'Will sub order',
      0
    );
    const willSubOrderInput = willSubOrder.querySelector('input');
    if (willSubOrderInput) {
      willSubOrderInput.style.width = '10ch';
      willSubOrderInput.style.maxWidth = '10ch';
    }
    const willSubOrderDiv = willSubOrder.querySelector('.input-group') || willSubOrder;
    if (willSubOrderDiv?.style) {
      willSubOrderDiv.style.width = 'fit-content';
      willSubOrderDiv.style.maxWidth = 'fit-content';
    }

    const activeYn = await comp.toggle(
      `woBreakdown_${id}_active_yn`,
      !!item.active_yn,
      'Active'
    );
    const includeSoleSpouseYn = await comp.toggle(
      `woBreakdown_${id}_include_sole_spouse_yn`,
      !!item.include_sole_spouse_yn,
      'Include sole spouse'
    );
    const includeNonSoleSpouseYn = await comp.toggle(
      `woBreakdown_${id}_include_non_sole_spouse_yn`,
      !!item.include_non_sole_spouse_yn,
      'Include non-sole spouse'
    );

    const topRow = U.divAtt('div', 'class', 'flex justify-between items-end gap-4 flex-wrap mb-2');
    topRow.appendChild(willSubOrder);
    const togglesWrap = U.divAtt('div', 'class', 'flex items-end gap-6 flex-wrap shrink-0');
    togglesWrap.appendChild(activeYn);
    togglesWrap.appendChild(includeSoleSpouseYn);
    togglesWrap.appendChild(includeNonSoleSpouseYn);
    topRow.appendChild(togglesWrap);
    form.appendChild(topRow);

    const listOrderHeading = await comp.inputText(
      `woBreakdown_${id}_list_order_heading`,
      item.list_order_heading,
      'List order heading',
      0
    );
    const description = await comp.inputText(
      `woBreakdown_${id}_description`,
      item.description ?? item.descriptio ?? '',
      'Description',
      0
    );
    const body = await comp.textarea(`woBreakdown_${id}_body`, item.body, 'Body', 0);
    const subBody = await comp.textarea(`woBreakdown_${id}_sub_body`, item.sub_body, 'Sub body', 0);

    grid.appendChild(idField);
    grid.appendChild(listOrderHeading);
    grid.appendChild(description);
    grid.appendChild(body);
    grid.appendChild(subBody);
    form.appendChild(grid);

    const actions = U.divAtt('div', 'class', 'flex justify-end mt-4');
    const updateBtn = U.divAtt('button', 'class', 'btn');
    updateBtn.type = 'button';
    updateBtn.textContent = 'Update';
    updateBtn.setAttribute('onclick', `thsPage.updateElementBreakdown(${id})`);
    actions.appendChild(updateBtn);
    form.appendChild(actions);

    return form;
  },

  async updateElementBreakdown(woWillElementsId) {
    const vars = {
      wo_will_elements_id: woWillElementsId,
      will_sub_order: U.id(`woBreakdown_${woWillElementsId}_will_sub_order`)?.value,
      list_order_heading: U.id(`woBreakdown_${woWillElementsId}_list_order_heading`)?.value,
      description: U.id(`woBreakdown_${woWillElementsId}_description`)?.value,
      active_yn: U.id(`woBreakdown_${woWillElementsId}_active_yn`)?.checked,
      include_sole_spouse_yn: U.id(`woBreakdown_${woWillElementsId}_include_sole_spouse_yn`)?.checked,
      include_non_sole_spouse_yn: U.id(`woBreakdown_${woWillElementsId}_include_non_sole_spouse_yn`)?.checked,
      body: U.id(`woBreakdown_${woWillElementsId}_body`)?.value,
      sub_body: U.id(`woBreakdown_${woWillElementsId}_sub_body`)?.value,
    };

    try {
      const data = await O.PREP([await O.mPOST(EP_UPDATE_BREAKDOWN, vars)]);
      const res = data[EP_UPDATE_BREAKDOWN];
      comp.toast(res?.status || 'Updated');
    } catch {
      comp.toast('Unable to update element breakdown.');
    }
  },
};
